/**
 * Pure client-side web-push helpers: capability detection, VAPID key
 * conversion, and the soft-prompt visibility rule. The stateful subscribe /
 * unsubscribe flow lives in {@link ./use-push-subscription}.
 */

/** localStorage flag set when the user dismisses the matches-page prompt. */
export const PUSH_PROMPT_DISMISSED_KEY = "pf_push_prompt_dismissed";

/** localStorage flag set once the PWA onboarding modal was answered. */
export const PUSH_ONBOARDING_DONE_KEY = "pf_push_onboarding_done";

/**
 * Whether this browser can do web push. On iOS Safari `PushManager` only
 * exists for installed PWAs (16.4+), so no UA sniffing is needed.
 */
export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Decode a base64url VAPID public key into the `Uint8Array` that
 * `pushManager.subscribe` expects as `applicationServerKey`.
 */
export function urlBase64ToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

/**
 * Whether the app runs installed (home-screen launch). iOS Safari exposes the
 * legacy `navigator.standalone` flag; everything else reports through the
 * `display-mode` media query.
 */
export function isStandaloneDisplayMode(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in window.navigator &&
      (window.navigator as { standalone?: boolean }).standalone === true)
  );
}

export interface PushPromptInput {
  /** Browser supports service worker + PushManager + Notification. */
  supported: boolean;
  /** Server's VAPID public key; null = push disabled server-side. */
  vapidPublicKey: string | null;
  /** How many conversations the user has (prompt needs at least one). */
  conversationCount: number;
  /** Current `Notification.permission`; null while unknown (SSR / unsupported). */
  permission: NotificationPermission | null;
  /** User already dismissed the prompt ({@link PUSH_PROMPT_DISMISSED_KEY}). */
  dismissed: boolean;
}

/**
 * The matches-page soft prompt shows only when push can actually be enabled
 * right now and the user has never answered: at least one conversation, the
 * permission is still "default" (granted/denied both mean the question was
 * settled), and the prompt wasn't dismissed.
 */
export function shouldShowPushPrompt(input: PushPromptInput): boolean {
  return (
    input.supported &&
    input.vapidPublicKey !== null &&
    input.conversationCount > 0 &&
    input.permission === "default" &&
    !input.dismissed
  );
}

export interface PushOnboardingInput {
  /** App runs installed ({@link isStandaloneDisplayMode}). */
  standalone: boolean;
  /** Browser supports service worker + PushManager + Notification. */
  supported: boolean;
  /** Server's VAPID public key; null = push disabled server-side. */
  vapidPublicKey: string | null;
  /** Current `Notification.permission`; null while unknown (SSR / unsupported). */
  permission: NotificationPermission | null;
  /** The modal was already answered ({@link PUSH_ONBOARDING_DONE_KEY}). */
  done: boolean;
}

/**
 * The first-launch onboarding modal shows only inside the installed PWA, when
 * push can actually be enabled right now (supported + server key) and the
 * question was never settled: permission still "default" and the modal never
 * answered. Logged-out visitors never qualify — the VAPID key comes from an
 * authenticated endpoint, so it stays null for them.
 */
export function shouldShowPushOnboarding(input: PushOnboardingInput): boolean {
  return (
    input.standalone &&
    input.supported &&
    input.vapidPublicKey !== null &&
    input.permission === "default" &&
    !input.done
  );
}
